import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh } from '@ah/design/mobile';
import { Users } from 'lucide-react';
import { MUSIC_ARTISTS_QUERY } from '../shared/queries.ts';
import type { MusicArtist } from '../shared/types.ts';
import { ArtistsListSkeleton, ErrorRetry, MUSIC_TABS } from './_parts.tsx';

export function MusicArtists() {
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['music', 'artists'],
    queryFn: async () => {
      const res = await gql.request<{ musicArtists: MusicArtist[] }>(MUSIC_ARTISTS_QUERY);
      return res.musicArtists;
    },
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Music" />
      <PillTabs tabs={MUSIC_TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={() => refetch()}>
        {isLoading ? (
          <ArtistsListSkeleton />
        ) : error ? (
          <ErrorRetry label="Could not load artists." onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Users size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No artists seeded yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.map((a) => (
              <Link
                key={a.id}
                to={`/music/artists/${a.slug}`}
                className="block bg-card rounded-xl border border-border p-3 active:bg-accent"
              >
                <p className="text-sm font-semibold leading-snug">{a.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{a.tagline}</p>
                <p className="text-[11px] text-muted-foreground/70 mt-1">
                  {a.songCount} {a.songCount === 1 ? 'song' : 'songs'}
                  {a.style ? <span> · {a.style}</span> : null}
                </p>
              </Link>
            ))}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}
